<template>
  <div class="pin-pad">
    <div class="pin-dots">
      <span
        v-for="i in length"
        :key="i"
        class="pin-dot"
        :class="{ filled: i <= modelValue.length, error: shake }"
      />
    </div>

    <div v-if="hint" class="pin-hint">{{ hint }}</div>

    <div class="pin-keys">
      <button
        v-for="n in keypad"
        :key="n.key"
        type="button"
        class="pin-key"
        :class="{ 'pin-key-action': n.action }"
        :disabled="
          disabled || (n.action === 'enter' && modelValue.length < length)
        "
        @click="onKey(n)"
      >
        <el-icon v-if="n.icon" :size="26">
          <component :is="n.icon" />
        </el-icon>
        <span v-else>{{ n.label }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { Back, Delete } from "@element-plus/icons-vue";

const props = withDefaults(
  defineProps<{
    modelValue: string;
    length?: number;
    disabled?: boolean;
    hint?: string;
  }>(),
  { length: 6, disabled: false, hint: "" },
);

const emit = defineEmits<{
  "update:modelValue": [string];
  filled: [string];
}>();

const shake = ref(false);

interface Key {
  key: string;
  label?: string;
  icon?: typeof Back | typeof Delete;
  action?: "backspace" | "clear" | "enter";
}

const keypad = computed<Key[]>(() => [
  { key: "1", label: "1" },
  { key: "2", label: "2" },
  { key: "3", label: "3" },
  { key: "4", label: "4" },
  { key: "5", label: "5" },
  { key: "6", label: "6" },
  { key: "7", label: "7" },
  { key: "8", label: "8" },
  { key: "9", label: "9" },
  { key: "clear", label: "ล้าง", action: "clear" },
  { key: "0", label: "0" },
  { key: "back", icon: Back, action: "backspace" },
]);

function onKey(k: Key): void {
  if (props.disabled) return;
  if (k.action === "backspace") {
    emit("update:modelValue", props.modelValue.slice(0, -1));
    return;
  }
  if (k.action === "clear") {
    emit("update:modelValue", "");
    return;
  }
  if (props.modelValue.length >= props.length) return;
  const next = props.modelValue + k.key;
  emit("update:modelValue", next);
  if (next.length === props.length) emit("filled", next);
}

defineExpose({
  shakeError(): void {
    shake.value = true;
    setTimeout(() => (shake.value = false), 500);
  },
});

// Reset shake whenever value changes after error
watch(
  () => props.modelValue,
  () => {
    if (shake.value) shake.value = false;
  },
);
</script>

<style scoped>
.pin-pad {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-top: 4px;
  width: 100%;
}
.pin-dots {
  display: flex;
  gap: 14px;
  margin: 4px 0 8px;
  justify-content: center;
}
.pin-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2.5px solid #cbd5e1;
  background: transparent;
  transition:
    background 0.15s,
    border-color 0.15s,
    transform 0.1s;
}
.pin-dot.filled {
  background: var(--c-primary);
  border-color: var(--c-primary);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15);
}
.pin-dot.error {
  border-color: var(--c-danger);
  background: var(--c-danger);
  animation: shake 0.4s;
}
@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-6px);
  }
  75% {
    transform: translateX(6px);
  }
}
.pin-hint {
  font-size: 13px;
  color: #6b7280;
  text-align: center;
  min-height: 18px;
}
.pin-keys {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  /* Use min(...) so keypad comfortably occupies up to 340px */
  width: min(340px, 100%);
  margin: 0 auto;
  box-sizing: border-box;
}
.pin-key {
  /* Height adapts to viewport, with larger, more comfortable tap target */
  height: clamp(52px, 7.5vh, 64px);
  border: 1px solid #e0ecfa;
  border-radius: 14px;
  background: #ffffff;
  color: var(--c-ink);
  font-size: clamp(24px, 6vw, 28px);
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.1s,
    transform 0.05s,
    box-shadow 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.06);
}
.pin-key:hover:not(:disabled) {
  background: #f0f9ff;
  border-color: var(--c-primary-soft);
}
.pin-key:active:not(:disabled) {
  background: #dbeafe;
  border-color: var(--c-primary);
  color: var(--c-primary-deep);
  transform: scale(0.96);
}
.pin-key:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.pin-key-action {
  font-size: 15px;
  color: var(--c-ink-soft);
  font-weight: 600;
}
@media (max-width: 380px) {
  .pin-dots {
    gap: 10px;
  }
  .pin-dot {
    width: 15px;
    height: 15px;
    border-width: 2px;
  }
  .pin-keys {
    gap: 8px;
    max-width: 100%;
  }
  .pin-key {
    height: clamp(48px, 6.8vh, 56px);
    font-size: 22px;
    border-radius: 12px;
  }
  .pin-key-action {
    font-size: 14px;
  }
}
</style>
